import { useState } from 'react';

interface SidebarItem {
  id: string;
  label: string;
  children?: SidebarItem[];
}

const TOC: SidebarItem[] = [
  {
    id: 'getting-started',
    label: 'GETTING STARTED',
    children: [
      { id: 'installation', label: 'Installation' },
      { id: 'quickstart', label: 'Quick Start' },
    ],
  },
  {
    id: 'gpu',
    label: 'GPU',
    children: [
      { id: 'gpu-status', label: 'gpu_status()' },
      { id: 'gpu-what-runs', label: 'What Runs on GPU' },
      { id: 'gpu-auto', label: 'Auto-activation' },
      { id: 'gpu-compat', label: 'Compatibility Rules' },
    ],
  },
  {
    id: 'data',
    label: 'DATA',
    children: [
      { id: 'data-list', label: 'list_datasets() / list_symbols()' },
      { id: 'data-prices', label: 'prices()' },
      { id: 'data-load', label: 'load()' },
      { id: 'data-returns', label: 'returns() / cumulative_returns()' },
      { id: 'data-from-csv', label: 'from_csv()' },
      { id: 'data-configure', label: 'configure() / clear_cache()' },
      { id: 'data-providers', label: 'Providers' },
    ],
  },
  {
    id: 'signals',
    label: 'SIGNALS',
    children: [
      { id: 'signals-trend', label: 'Trend' },
      { id: 'signals-momentum', label: 'Momentum' },
      { id: 'signals-mean-reversion', label: 'Mean Reversion' },
      { id: 'signals-combine', label: 'combine()' },
      { id: 'signals-multi', label: 'Multi-Symbol' },
    ],
  },
  {
    id: 'backtest',
    label: 'BACKTEST',
    children: [
      { id: 'backtest-run', label: 'run()' },
      { id: 'backtest-result', label: 'BacktestResult' },
      { id: 'backtest-costs', label: 'CostModel' },
    ],
  },
  {
    id: 'factors',
    label: 'FACTORS',
    children: [
      { id: 'factors-performance', label: 'Performance' },
      { id: 'factors-risk', label: 'Risk' },
    ],
  },
  {
    id: 'viz',
    label: 'VISUALIZATION',
    children: [
      { id: 'viz-tearsheet', label: 'tearsheet()' },
      { id: 'viz-candles', label: 'candles()' },
      { id: 'viz-heatmaps', label: 'Heatmaps' },
      { id: 'viz-strip', label: 'strip()' },
      { id: 'viz-theming', label: 'Theming' },
    ],
  },
  { id: 'env', label: 'ENVIRONMENT' },
  { id: 'config', label: 'CONFIG' },
  { id: 'exceptions', label: 'EXCEPTIONS' },
  {
    id: 'sweeps',
    label: 'GPU SWEEPS',
    children: [
      { id: 'sweeps-auth', label: 'Getting a key' },
      { id: 'sweeps-endpoints', label: 'Endpoints' },
      { id: 'sweeps-plan', label: 'The plan' },
      { id: 'sweeps-cli', label: 'Estimate, launch, wait' },
    ],
  },
  {
    id: 'feeds',
    label: 'SIGNAL FEEDS',
    children: [
      { id: 'feeds-endpoints', label: 'Endpoints' },
      { id: 'feeds-create', label: 'Create a feed' },
      { id: 'feeds-emissions', label: 'Dry run, then send' },
      { id: 'feeds-hmac', label: 'HMAC delivery' },
      { id: 'feeds-audit', label: 'Audit provenance' },
    ],
  },
  {
    id: 'cli',
    label: 'CLI',
    children: [
      { id: 'cli-contract', label: 'Output contract' },
      { id: 'cli-exit-codes', label: 'Exit codes' },
      { id: 'cli-auth', label: 'Auth' },
      { id: 'cli-commands', label: 'Commands' },
    ],
  },
];

interface DocsSidebarProps {
  activeSection: string;
  onNavigate: (id: string) => void;
}

function SidebarContent({ activeSection, onNavigate }: DocsSidebarProps) {
  const isActive = (id: string) => activeSection === id;
  const isParentActive = (item: SidebarItem) =>
    isActive(item.id) || (item.children?.some(c => isActive(c.id)) ?? false);

  return (
    <nav className="text-left space-y-1">
      {TOC.map((group) => (
        <div key={group.id} className="mb-3">
          <button
            onClick={() => onNavigate(group.id)}
            className={`block w-full text-left text-xs font-semibold tracking-wider py-1.5 px-3 rounded transition-colors ${
              isParentActive(group)
                ? 'text-white'
                : 'text-gray-500 hover:text-gray-300'
            }`}
          >
            {group.label}
          </button>
          {group.children && (
            <div className="mt-0.5 space-y-0">
              {group.children.map((child) => (
                <button
                  key={child.id}
                  onClick={() => onNavigate(child.id)}
                  className={`block w-full text-left text-sm py-1 pl-3 border-l-2 transition-colors ${
                    isActive(child.id)
                      ? 'border-sky-500 text-white bg-sky-500/5'
                      : 'border-transparent text-gray-500 hover:text-gray-300 hover:border-gray-700'
                  }`}
                >
                  {child.label}
                </button>
              ))}
            </div>
          )}
        </div>
      ))}
    </nav>
  );
}

export default function DocsSidebar({ activeSection, onNavigate }: DocsSidebarProps) {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <>
      {/* Desktop sidebar */}
      <aside className="hidden lg:block w-64 shrink-0">
        <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-4 pb-8 scrollbar-thin">
          <SidebarContent activeSection={activeSection} onNavigate={onNavigate} />
        </div>
      </aside>

      {/* Mobile TOC button */}
      <button
        onClick={() => setMobileOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-40 w-12 h-12 bg-sky-600 hover:bg-sky-500 text-white rounded-full shadow-lg flex items-center justify-center transition-colors"
        aria-label="Table of contents"
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </button>

      {/* Mobile slide-up sheet */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div
            className="absolute inset-0 bg-black/60"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute bottom-0 left-0 right-0 bg-gray-950 border-t border-gray-800 rounded-t-2xl max-h-[70vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-gray-300 uppercase tracking-wider">Contents</h3>
              <button
                onClick={() => setMobileOpen(false)}
                className="text-gray-500 hover:text-white"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <SidebarContent
              activeSection={activeSection}
              onNavigate={(id) => {
                setMobileOpen(false);
                onNavigate(id);
              }}
            />
          </div>
        </div>
      )}
    </>
  );
}
